<template>
  <div class="v-body">
    <canvas
      :width="canvasWidth"
      :height="canvasHeight"
      id="canvas"
      v-show="showCanvas"
      ref="canvasElement"
    ></canvas>

    <button
      v-if="canSwitchCamera"
      type="button"
      class="switch-camera-btn"
      title="切换摄像头"
      @click="switchCamera"
    >
      <i class="fa-solid fa-camera-rotate"></i>
    </button>
  </div>
</template>

<script>
// eslint-disable-next-line no-unused-vars
import adapter from 'webrtc-adapter'
import jsQR from 'jsqr'

/** 后置摄像头常见英文名 / 中文名（各厂商 WebView 文案不一，尽量宽松） */
const REAR_CAMERA_LABEL_RE =
  /rear|back|environment|后置|后摄|背摄|主摄|wide|广角|0\s*,\s*facing\s*back|facing\s*back|camera\s*2|camera2|fbcam/i
/** 前置 / 人脸 相关 */
const FRONT_CAMERA_LABEL_RE =
  /front|user|前置|前摄|人脸|自拍|ffc|1\s*,\s*facing\s*front|facing\s*front|camera\s*1(?!\d)/i

export default {
  name: "qrCodeScanner",
  props: {
    continuous: { type: Boolean, default: false },
    emitCooldownMs: { type: Number, default: 2000 },
  },
  data: () => ({
    video: document.createElement('video'),
    loadingMessage: '🎥 Unable to access video stream (please make sure you have a webcam enabled)',
    showCanvas: true,
    canvas2d: undefined,
    outputData: undefined,
    canvasWidth: 100,
    canvasHeight: 100,
    animitionId: null,
    videoArr: [],
    videoIndex: 0,
    lastEmittedData: undefined,
    lastEmittedTime: 0,
  }),
  computed: {
    canSwitchCamera() {
      return Array.isArray(this.videoArr) && this.videoArr.length > 1
    },
  },
  destroyed() {
    this.closeCamera()
    this.videoIndex = 0
  },
  mounted() {
    this.getCameraInfo()
  },
  methods: {
    switchCamera() {
      if (!this.canSwitchCamera) {
        return
      }
      this.videoIndex = (this.videoIndex + 1) % this.videoArr.length
      this.openCamera(this.videoArr[this.videoIndex].id)
    },
    /**
     * @param {string} id deviceId，空字符串表示首次打开：优先后置摄像头
     */
    openCamera(id) {
      if (id === '') {
        this.openInitialCamera()
        return
      }
      this.closeCamera()
      navigator.mediaDevices
        .getUserMedia({ video: { deviceId: { exact: id } } })
        .then((stream) => this.startScanningWithStream(stream))
        .catch((err) => this.showCameraError(err))
    },
    openInitialCamera() {
      this.closeCamera()
      const tryConstraints = [
        { video: { facingMode: { ideal: 'environment' } } },
        { video: { facingMode: { exact: 'environment' } } },
        { video: true },
      ]
      ;(async () => {
        let stream = null
        let lastErr = null
        for (const constraints of tryConstraints) {
          try {
            stream = await navigator.mediaDevices.getUserMedia(constraints)
            break
          } catch (e) {
            lastErr = e
          }
        }
        if (!stream) {
          this.showCameraError(lastErr)
          return
        }
        try {
          stream = await this.ensureRearFacingStream(stream)
        } catch (e) {
          this.showCameraError(e)
          return
        }
        await this.startScanningWithStream(stream)
      })()
    },
    /**
     * 部分机型（荣耀、部分平板 WebView）会忽略 facingMode 仍给前置；在已授权后根据 label / facingMode / 双头启发式切到后置。
     */
    async ensureRearFacingStream(stream) {
      await this.getCameraInfo()
      let current = stream
      let track = current.getVideoTracks()[0]
      if (!track) {
        return current
      }
      const rearId = this.pickRearDeviceId(track)
      const curId = track.getSettings && track.getSettings().deviceId
      if (!rearId || !curId || rearId === curId) {
        return current
      }
      const looksRear = this.trackLooksLikeRear(track)
      const looksFront = this.trackLooksLikeFront(track)
      const shouldSwap = looksFront || (!looksRear && this.shouldAssumeFrontIsWrong(track))
      if (!shouldSwap) {
        return current
      }
      current.getTracks().forEach((t) => t.stop())
      try {
        return await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: rearId } } })
      } catch (e) {
        try {
          return await navigator.mediaDevices.getUserMedia({ video: true })
        } catch (e2) {
          throw e2
        }
      }
    },
    pickRearDeviceId(track) {
      const curId = track.getSettings && track.getSettings().deviceId
      const arr = this.videoArr || []
      for (const d of arr) {
        if (!d.label) {
          continue
        }
        if (REAR_CAMERA_LABEL_RE.test(d.label) && !FRONT_CAMERA_LABEL_RE.test(d.label)) {
          return d.id
        }
      }
      for (const d of arr) {
        if (!d.label || FRONT_CAMERA_LABEL_RE.test(d.label)) {
          continue
        }
        if (!FRONT_CAMERA_LABEL_RE.test(d.label) && d.id !== curId) {
          return d.id
        }
      }
      if (arr.length === 2 && !arr[0].label && !arr[1].label && curId) {
        const other = arr.find((d) => d.id !== curId)
        if (other) {
          return other.id
        }
      }
      return null
    },
    shouldAssumeFrontIsWrong(track) {
      if ((this.videoArr || []).length < 2) {
        return false
      }
      const label = (track.label || '').trim()
      if (label) {
        return false
      }
      const settings = track.getSettings ? track.getSettings() : {}
      if (settings.facingMode === 'environment') {
        return false
      }
      return true
    },
    trackLooksLikeFront(track) {
      const settings = track.getSettings ? track.getSettings() : {}
      if (settings.facingMode === 'user') {
        return true
      }
      const label = track.label || ''
      return FRONT_CAMERA_LABEL_RE.test(label)
    },
    trackLooksLikeRear(track) {
      const settings = track.getSettings ? track.getSettings() : {}
      if (settings.facingMode === 'environment') {
        return true
      }
      const label = track.label || ''
      return REAR_CAMERA_LABEL_RE.test(label) && !FRONT_CAMERA_LABEL_RE.test(label)
    },
    async startScanningWithStream(stream) {
      this.video.srcObject = stream
      this.video.setAttribute('playsinline', true)
      try {
        await this.video.play()
      } catch (e) {
        /* 部分浏览器 play 拒绝不阻塞解码 */
      }
      await this.refreshVideoDevicesAndSync(stream)
      this.tick()
    },
    async refreshVideoDevicesAndSync(stream) {
      await this.getCameraInfo()
      const track = stream.getVideoTracks()[0]
      const deviceId = track && track.getSettings && track.getSettings().deviceId
      if (deviceId) {
        const idx = this.videoArr.findIndex((d) => d.id === deviceId)
        this.videoIndex = idx >= 0 ? idx : 0
      } else {
        this.videoIndex = 0
      }
    },
    showCameraError(err) {
      let msg = '无法打开摄像头'
      if (err && err.name === 'NotAllowedError') {
        msg = '无法访问摄像头，请授予权限'
      } else if (err && err.name === 'NotFoundError') {
        msg = '未检测到摄像头设备'
      } else if (err && err.message) {
        msg = `无法打开摄像头：${err.message}`
      }
      if (this.$message) {
        this.$message.error(msg)
      } else {
        console.error(msg, err)
      }
    },
    closeCamera() {
      if (typeof this.canvas2d !== 'undefined') {
        this.canvas2d.clearRect(0, 0, this.canvasWidth, this.canvasHeight)
      }
      this.cancelAnimation()
      if (this.video.srcObject) {
        this.video.srcObject.getTracks().forEach(function (track) {
          track.stop()
        })
      }
      this.video.srcObject = null
    },
    getCameraInfo() {
      return navigator.mediaDevices
        .enumerateDevices()
        .then((devices) => {
          this.videoArr = []
          devices.forEach((device) => {
            if (device.kind === 'videoinput') {
              this.videoArr.push({ label: device.label, id: device.deviceId })
            }
          })
        })
        .catch((err) => {
          console.log(err.name + ':' + err.message)
        })
    },
    tick() {
      this.loadingMessage = '⌛ Loading video...'
      if (this.video.readyState === this.video.HAVE_ENOUGH_DATA) {
        this.canvasHeight = this.video.videoHeight
        this.canvasWidth = this.video.videoWidth
        !this.canvas2d && (this.canvas2d = this.$refs.canvasElement.getContext('2d'))
        this.canvas2d.drawImage(this.video, 0, 0, this.canvasWidth, this.canvasHeight)
        var imageData = this.canvas2d.getImageData(0, 0, this.canvasWidth, this.canvasHeight)
        var code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'dontInvert',
        })
        if (code) {
          this.drawLine(code.location.topLeftCorner, code.location.topRightCorner, '#FF3B58')
          this.drawLine(code.location.topRightCorner, code.location.bottomRightCorner, '#FF3B58')
          this.drawLine(code.location.bottomRightCorner, code.location.bottomLeftCorner, '#FF3B58')
          this.drawLine(code.location.bottomLeftCorner, code.location.topLeftCorner, '#FF3B58')
          // 与旧版行为保持一致：无论是正常解码还是兜底重解，都把结果原样抛给业务层（含空串）
          const payload = this.decodePayload(code)
          if (this.continuous) {
            const now = Date.now()
            if (payload !== this.lastEmittedData || now - this.lastEmittedTime > this.emitCooldownMs) {
              this.lastEmittedData = payload
              this.lastEmittedTime = now
              this.outputData = payload
              this.$emit('getCodeData', payload)
            }
          } else {
            this.outputData = payload
            this.$emit('getCodeData', payload)
            this.closeCamera()
            return
          }
        } else {
          this.outputData = undefined
        }
      }
      this.animitionId = requestAnimationFrame(this.tick)
    },
    /**
     * jsQR 1.x 的 byte 模式固定按 UTF-8 解码，遇到 GBK/GB18030 编码的二维码会静默失败，
     * 导致 code.data 变成空串。这里改用原始字节重新解码：先 UTF-8，失败再退回 GB18030。
     */
    decodePayload(code) {
      let text = code.data || ''
      if (!text) {
        // jsQR 的 byte 模式固定按 UTF-8 解码，失败会静默返回空串，这里用原始字节重试
        const bytes = code.binaryData && code.binaryData.length ? Uint8Array.from(code.binaryData) : null
        if (bytes && typeof TextDecoder !== 'undefined') {
          for (const encoding of ['utf-8', 'gb18030']) {
            try {
              text = new TextDecoder(encoding, { fatal: true }).decode(bytes)
              if (text) {
                break
              }
            } catch (e) {
              /* 该编码解不出来，换下一个 */
            }
          }
        }
      }
      // 去掉可能存在的 BOM，否则 JSON.parse 会直接报错
      return text.replace(/^\uFEFF/, '')
    },
    drawLine(begin, end, color) {
      this.canvas2d.beginPath()
      this.canvas2d.moveTo(begin.x, begin.y)
      this.canvas2d.lineTo(end.x, end.y)
      this.canvas2d.lineWidth = 4
      this.canvas2d.strokeStyle = color
      this.canvas2d.stroke()
    },
    resetOutputData() {
      this.outputData = undefined
      this.canvas2d = undefined
      this.lastEmittedData = undefined
      this.lastEmittedTime = 0
      console.log('执行重置数据操作')
    },
    cancelAnimation() {
      if (this.animitionId != null) {
        cancelAnimationFrame(this.animitionId)
        this.animitionId = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.v-body {
  font-family: "Ropa Sans", sans-serif;
  color: #333;
  max-width: 300px;
  margin: 0 auto;
  position: relative;
}
.switch-camera-btn {
  position: absolute;
  right: 10px;
  bottom: 10px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: rgba(30, 82, 116, 0.75);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  z-index: 2;
}
.switch-camera-btn:hover {
  background: rgba(30, 82, 116, 0.9);
}
#githubLink {
  position: absolute;
  right: 0;
  top: 12px;
  color: #2d99ff;
}
h1 {
  margin: 10px 0;
  font-size: 40px;
}
#loadingMessage {
  text-align: center;
  padding: 40px;
  background-color: #eee;
}
#canvas {
  width: 100%;
}
#output {
  margin-top: 20px;
  background: #eee;
  padding: 10px;
  padding-bottom: 0;
}
#output div {
  padding-bottom: 10px;
  word-wrap: break-word;
}
#noQRFound {
  text-align: center;
}
</style>
